import React from "react";
import { extractNumericValue, USERS_LOCALE } from "./utils";
import currencyData from "@/statics/currency.json";

const CURRENCY_SYMBOL_MAP = new Map(
  currencyData.map(({ currencyCode, symbol }) => [currencyCode, symbol])
);

// AED and SAR have no Unicode symbol, so they are drawn with a ligature font
// ("CurrencySymbols") applied via the .aed-symbol / .sar-symbol classes.
const CUSTOM_FONT_CURRENCIES = new Set(["AED", "SAR"]);

export const usesCustomFontSymbol = (currency: string) =>
  CUSTOM_FONT_CURRENCIES.has(currency);

/**
 * The symbol to DRAW for a currency — not necessarily the one stored in
 * currency.json. For AED/SAR this is the code itself ("AED"), because the ligature
 * font turns that text into the glyph; it must be rendered with the matching
 * .aed-symbol / .sar-symbol class or it will read as literal letters.
 * Use OpticksCurrencySymbol rather than calling this directly.
 */
export const getCurrencySymbol = (currency: string): string =>
  usesCustomFontSymbol(currency)
    ? currency
    : (CURRENCY_SYMBOL_MAP.get(currency) ?? currency);

export interface CurrencyPart {
  value: string;
  /** True for the currency symbol itself, so a renderer can style it. */
  isSymbol: boolean;
}

/**
 * The renderer-agnostic form of a formatted amount, so HTML and SVG can be built
 * from one source. `formatCurrency` renders it to spans; the chart axis renders the
 * same parts to <tspan>, which is why the axis and the tooltip cannot drift apart.
 *
 * `locale` defaults to the detected browser locale and should be left alone in the
 * app. The currency CODE is not locale-derived; it comes from account settings.
 */
export const formatCurrencyParts = (
  value: number = 0,
  currency: string,
  decimals: boolean = true,
  locale: string = USERS_LOCALE
): CurrencyPart[] => {
  const parts = new Intl.NumberFormat(locale, {
    useGrouping: true,
    style: "currency",
    currency: currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: decimals ? 2 : 0,
    maximumFractionDigits: decimals ? 2 : 0,
  }).formatToParts(value);

  const customSymbol = CURRENCY_SYMBOL_MAP.get(currency);

  return parts
    .filter(
      (part, index) =>
        // Drop the space Intl puts after the AED symbol.
        !(
          currency === "AED" &&
          part.type === "literal" &&
          index > 0 &&
          parts[index - 1]?.type === "currency"
        )
    )
    .map((part) => {
      if (part.type !== "currency") {
        return { value: part.value, isSymbol: false };
      }

      // The ligature font renders the code itself ("AED") as the new glyph.
      if (usesCustomFontSymbol(currency)) {
        return { value: currency, isSymbol: true };
      }

      return { value: customSymbol ?? part.value, isSymbol: true };
    });
};

export const formatCurrency = (
  value: number = 0,
  currency: string,
  decimals: boolean = true,
  locale: string = USERS_LOCALE
): string | React.JSX.Element => {
  const parts = formatCurrencyParts(value, currency, decimals, locale);

  if (usesCustomFontSymbol(currency)) {
    return (
      <span>
        {parts.map((part, index) => (
          <span
            key={index}
            className={
              part.isSymbol ? `${currency.toLowerCase()}-symbol` : "part-value"
            }
          >
            {part.value}
          </span>
        ))}
      </span>
    );
  }

  return parts.map((part) => part.value).join("");
};

export const getNumericValue = (val: string | React.ReactNode): number => {
  if (typeof val === "string") {
    return extractNumericValue(val);
  }

  const children = (val as React.ReactElement<{ children?: React.ReactNode }>)
    ?.props?.children;
  if (Array.isArray(children)) {
    const text = children
      .map((child) => {
        if (typeof child === "string") return child;
        if (child?.props?.children) return child.props.children;
        return "";
      })
      .join("");
    return extractNumericValue(text);
  }
  return 0;
};
